import React, { memo, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { Marker } from "./Marker";
import { Button, Paper, styled } from "@mui/material";
import { GoogleMap, MarkerClustererF } from "@react-google-maps/api";
import { MapLoadingSpinner } from "./MapLoadingSpinner";
import { When } from "react-if";
import { LightPointsErrorBox } from "./LightPointsErrorBox";
import { HintInfoBox } from "./HintInfoBox";
import { useGeoMap } from "../../context/GeoMapContext";
import { LightPoint } from "../../models/LightPoint";

interface GeoMapProps {
  elevation?: number;
  style?: React.CSSProperties;
  showMaxMarkerInfo?: boolean;
  showAddNewLightPointInfo?: boolean;
  onShowFaultReport?: (id: string) => void;
  defaultFetchType: "defaultCenter" | "customerScope";
}

export const GeoMap = memo(({ onShowFaultReport, ...props }: GeoMapProps) => {
  const {
    allLightPoints,
    mapRef,
    mapIsLoading,
    isOnMoveMapEffectsBlocked,
    center,
    fetchLightPoints,
    lightPointsError,
    fitMapBounds,
    selectedMarker,
  } = useGeoMap();

  const [controlDiv, setControlDiv] = useState<HTMLDivElement | null>(null);

  const onLoad = useCallback(
    async (map: google.maps.Map) => {
      mapRef.current = map;

      const div = document.createElement("div");
      map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(div);
      setControlDiv(div);

      isOnMoveMapEffectsBlocked.current = true;
      if (allLightPoints.length) {
        fitMapBounds({ lightPoints: allLightPoints });
        return;
      }
      const initialLightPoints = await fetchLightPoints();
      fitMapBounds({ lightPoints: initialLightPoints });
    },
    [fetchLightPoints, allLightPoints, isOnMoveMapEffectsBlocked, mapRef, fitMapBounds]
  );

  function onUnmount() {
    mapRef.current = undefined;
    isOnMoveMapEffectsBlocked.current = false;
    setControlDiv(null);
  }

  return (
    <Paper elevation={props.elevation} className="flex relative flex-col flex-1" style={{ ...props.style, touchAction: "none" }}>
      <When condition={lightPointsError}>
        <LightPointsErrorBox onClose={fetchLightPoints} />
      </When>

      <GoogleMap
        center={center.current}
        onUnmount={onUnmount}
        zoom={10}
        onLoad={onLoad}
        mapContainerStyle={{ width: "100%", height: "100%", border: "1px solid #dfdfdf", borderRadius: "8px" }}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          fullscreenControl: true,
          gestureHandling: "greedy",
          styles: [
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [{ visibility: "off" }],
            },
          ],
        }}
      >
        <LightPointsAsMarker
          lightPoints={allLightPoints}
          selectedMarker={selectedMarker}
          onShowFaultReport={onShowFaultReport}
        />

        {/* RENDER PORTAL: This injects the button into the Google Map DOM safely */}
        {controlDiv && mapRef.current &&
          createPortal(<SatelliteButton map={mapRef.current} />, controlDiv)
        }
      </GoogleMap>

      <When condition={!!props.showAddNewLightPointInfo}>
        <HintInfoBox label="Leuchtstelle nicht gefunden? Einfach auf die Karte klicken und neuen Standort festlegen" />
      </When>
      <MapLoadingSpinner isLoading={mapIsLoading} />
    </Paper>
  );
});

interface LightPointMarkersProps {
  onShowFaultReport?: (id: string) => void;
  lightPoints: LightPoint[];
  selectedMarker?: LightPoint;
}

const LightPointsAsMarker = memo(({ onShowFaultReport, lightPoints, selectedMarker }: LightPointMarkersProps) => {
  return !lightPoints?.length ? null : (
    <MarkerClustererF
      options={{
        gridSize: 60,
        maxZoom: 16,
        minimumClusterSize: 2,
        zoomOnClick: true,
      }}
    >
      {(clusterer) => (
        <>
          {lightPoints.map((lightPoint) => (
            <Marker
              key={lightPoint.number}
              clusterer={selectedMarker?.number === lightPoint.number ? undefined : clusterer}
              lightingPoint={lightPoint}
              onShowFaultReport={onShowFaultReport}
            />
          ))}
        </>
      )}
    </MarkerClustererF>
  );
});

export const SatelliteButton = ({ map }: { map: google.maps.Map }) => {
  const [onSatelliteView, setOnSatelliteView] = useState(false);

  function handleOnClick() {
    if (onSatelliteView) {
      map.setMapTypeId("roadmap");
    } else {
      map.setMapTypeId("satellite");
    }
    setOnSatelliteView(!onSatelliteView);
  }

  return (
    <WhiteButton variant="contained" onClick={handleOnClick}>
      {onSatelliteView ? "Straßenansicht" : "Satellitenansicht"}
    </WhiteButton>
  );
};

const WhiteButton = styled(Button)({
  color: "rgb(102, 102, 102)",
  backgroundColor: "white",
  fontWeight: 600,
  width: 180,
  marginBottom: "23px",
  "&:hover": {
    color: "black",
    backgroundColor: "white",
  },
});

export default GeoMap;
